import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Inspection } from "../../../api";
import { useAuth } from "../../../hooks";
import { ChecklistExecution } from "../../../components/Admin/Checklist/ChecklistExecution";
import { getOfflineDraft } from "../../../utils/inspectionOffline";

const inspCtrl = new Inspection();

export function InspectRun() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { accessToken } = useAuth();

  const [inspection, setInspection] = useState(null);
  const [template,   setTemplate]   = useState(null);
  const [geo,        setGeo]        = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState(null);
  const [done,       setDone]       = useState(null); // null | "completed" | "closed"

  useEffect(() => {
    if (!accessToken || !id) return;
    let cancelled = false;

    (async () => {
      try {
        const insp  = await inspCtrl.getInspection(accessToken, id);
        const tplId = insp.template?._id || insp.template;
        const tpl   = await inspCtrl.getTemplate(accessToken, tplId);

        // Merge offline draft if one was saved while network was unavailable
        const draft = await getOfflineDraft(id).catch(() => null);
        if (draft?.payload?.responses) {
          insp.responses = draft.payload.responses;
        }

        if (!cancelled) { setInspection(insp); setTemplate(tpl); }
      } catch {
        if (!cancelled) setErr("No se pudo cargar la inspección. Verificá tu conexión.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, accessToken]);

  // Capture GPS once on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => setGeo({ lat: coords.latitude, lon: coords.longitude }),
      () => {}
    );
  }, []);

  if (loading) return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <style>{`@keyframes ir-spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ color: "#555", margin: 0 }}>Cargando inspección…</p>
    </div>
  );

  if (err) return (
    <div style={styles.center}>
      <span style={{ fontSize: "2rem" }}>⚠️</span>
      <p style={{ color: "#555", textAlign: "center", margin: "0.5rem 0 1rem" }}>{err}</p>
      <button style={styles.btn} onClick={() => navigate("/admin/checklist")}>Volver</button>
    </div>
  );

  // No confiar en el historial del navegador para "cerrar" — si la página se
  // abrió directo desde el QR no hay una entrada previa a la que volver.
  if (done) return (
    <div style={styles.center}>
      <span style={{ fontSize: "2.2rem" }}>{done === "completed" ? "✅" : "👋"}</span>
      <p style={{ color: "#333", textAlign: "center", margin: "0.5rem 0 1rem", fontWeight: 600 }}>
        {done === "completed" ? "Inspección completada" : "Salió sin completar — el borrador quedó guardado"}
      </p>
      <button style={styles.btn} onClick={() => navigate("/admin/checklist")}>
        Ir al listado
      </button>
    </div>
  );

  return (
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <ChecklistExecution
        inspection={inspection}
        template={template}
        geolocation={geo}
        autoSave
        close={() => setDone("closed")}
        onComplete={() => setDone("completed")}
      />
      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
}

const styles = {
  center: {
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    padding: "2rem",
    fontFamily: "system-ui, sans-serif",
  },
  spinner: {
    width: 38,
    height: 38,
    border: "3px solid #BFDBFE",
    borderTopColor: "#1A5FA0",
    borderRadius: "50%",
    animation: "ir-spin 0.75s linear infinite",
  },
  btn: {
    padding: "0.55rem 1.5rem",
    background: "#1A5FA0",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 600,
  },
};
